/**
 * Sorts two price objects based on their max and min prices.
 *
 * @param {Object} aPrice - The first price object to compare.
 * @param {Object} bPrice - The second price object to compare.
 * @returns {Number} A negative value if `aPrice` should come before `bPrice`,
 *                   a positive value if `aPrice` should come after `bPrice`,
 *                   or 0 if they are equal.
 */
const comparePrices = (aPrice, bPrice) => {
  const aMax = parseInt(aPrice.offerPriceMax);
  const aMin = parseInt(aPrice.offerPriceMin);
  const bMax = parseInt(bPrice.offerPriceMax);
  const bMin = parseInt(bPrice.offerPriceMin);

  if (aMax > bMax) return -1;
  if (aMax < bMax) return 1;
  if (aMin > bMin) return -1;
  if (aMin < bMin) return 1;
  return 0;
};

/**
 * Sorts swatch groups by their pricing information.
 *
 * @param {Array<Object>} swatchGroups - An array of swatch group objects.
 * @returns {Array<Object>} The sorted array of swatch group objects.
 */
export function sortedSwatchGroups(swatchGroups = []) {
  if (swatchGroups.length <= 1) return swatchGroups;

  return [...swatchGroups].sort((a, b) => {
    const aPrice = a.pricing;
    const bPrice = b.pricing;
    const aOnSpecial = aPrice.onSpecial !== undefined;
    const bOnSpecial = bPrice.onSpecial !== undefined;

    // If one is special and the other isn't, prioritize regular pricing
    if (aOnSpecial !== bOnSpecial) return aOnSpecial ? 1 : -1;

    // Both have same type of pricing (either both special or neither)
    if (!aPrice.offerPriceRange || !bPrice.offerPriceRange) return 0;

    return comparePrices(aPrice, bPrice);
  });
}

/**
 * Finds the indices of the swatch that matches the main image.
 *
 * @param {string} mainImage - The URL of the main image.
 * @param {Array<{ swatches: Array<{ fullImage: string }>}>} swatchGroups - An array of swatch groups.
 * @returns {{ swatchGroupIndex: number, swatchIndex: number } | null} - The indices of the matching swatch or null if not found.
 */
export const getSwatchIndices = (mainImage, swatchGroups) => {
  const mainImageHash = mainImage?.split('_zi_')?.[1]?.split('__')?.[0];

  let realIndex = -1;
  for (const [groupIndex, group] of swatchGroups.entries()) {
    for (const [swatchIndex, swatch] of group.swatches.entries()) {
      realIndex++;
      const imageHash = swatch.fullImage.split('_zi_')?.[1];
      if (imageHash && imageHash.startsWith(mainImageHash)) {
        return { swatchGroupIndex: groupIndex, realIndex, swatchIndex };
      }
    }
  }

  return null;
};

export function getSwiperSettings(siteFormat) {
  switch (siteFormat) {
    case 'desktop':
      return {
        navigation: true,
        slidesPerView: 6,
      };
    case 'mobile':
      return {
        slidesPerView: 6.5,
        breakpoints: {
          950: {
            slidesPerView: 10.5,
          },
          750: {
            slidesPerView: 8.5,
          },
          550: {
            slidesPerView: 7.5,
          },
          400: {
            slidesPerView: 6.5,
          },
        },
      };
    default:
      return {
        slidesPerView: 5.5,
        breakpoints: {
          1366: {
            slidesPerView: 5.5,
          },
          1024: {
            slidesPerView: 5.5,
          },
          768: {
            slidesPerView: 5.5,
          },
          480: {
            slidesPerView: 5.5,
          },
        },
      };
  }
}
